/* import __COLOCATED_TEMPLATE__ from './confluence.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type ConfluenceSite, type ConfluenceSpace } from 'embercom/services/knowledge-hub-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { taskFor } from 'ember-concurrency-ts';
import type ImportSource from 'embercom/models/import-service/import-source';
import type IntlService from 'embercom/services/intl';
import { SyncSteps } from 'embercom/components/knowledge-hub/filterable-list';
import { type TaskGenerator } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import { SourceType, SyncBehaviorType } from 'embercom/models/import-service/import-source';
import { type FolderTreeItem } from 'embercom/components/knowledge-hub/folder-selector';
import { get } from 'embercom/lib/ajax';

interface Signature {
  Args: {
    modal: any;
    title?: string;
    onNavigationBack?: () => void;
    onNavigationNext?: (viewId?: string) => void;
    showBackButton?: boolean;
    onModalClose: () => void;
    setSyncStep: (step: SyncSteps) => void;
    existingImportSource?: ImportSource;
    isReconfiguring?: boolean;
    isReauthenticating?: boolean;
  };
}

export default class ConfluenceSyncModal extends Component<Signature> {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare openCenteredWindowService: any;
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare appService: AppService;
  @service declare intercomEventService: any;

  @tracked importSource: ImportSource | undefined;
  @tracked selectedSite: ConfluenceSite | undefined;
  @tracked selectedSpace: ConfluenceSpace | undefined;
  @tracked sites: ConfluenceSite[] = [];
  @tracked spaces: ConfluenceSpace[] = [];

  @tracked folders: FolderTreeItem[] = [];
  @tracked selectedFolderIds: Set<string> = new Set();
  @tracked syncBehavior: SyncBehaviorType = SyncBehaviorType.Sync;
  @tracked useV2 = false;
  @tracked previousUseV2Value = false;

  get isLoadingOauth() {
    return taskFor(this.startConfluenceAuth).isRunning;
  }

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    if (this.args.existingImportSource) {
      this.importSource = this.args.existingImportSource;
    }

    // For new setups, default to V2
    // For reconfiguring, respect the existing setting
    this.useV2 = this.importSource?.context?.use_v2 ?? true;

    this.previousUseV2Value = this.useV2;

    taskFor(this.startConfluenceAuth).perform();
  }

  get selectedSiteId() {
    return this.selectedSite?.id;
  }

  get selectedSpaceId() {
    return this.selectedSpace?.id;
  }

  get importSourceId() {
    return this.importSource?.id;
  }

  get selectedFolderIdsArray() {
    return Array.from(this.selectedFolderIds);
  }

  get sitesGroupList() {
    return [
      {
        items: this.sites.map((site: ConfluenceSpace) => ({
          text: site.name,
          value: site.id,
        })),
      },
    ];
  }

  get selectedSiteLabel() {
    let selectedSite = this.sites.findBy('id', this.selectedSiteId);
    if (!selectedSite) {
      return this.intl.t('knowledge-hub.sync-modal.confluence.select-site-dropdown');
    }

    return selectedSite.name;
  }

  @action
  selectSite(siteId: string) {
    this.selectedSite = this.sites.findBy('id', siteId);
    this.resetSpaceSelection();

    this.loadSpaces();
  }

  get selectedSpaceLabel() {
    if (this.isLoadingSpaces) {
      return this.intl.t('knowledge-hub.sync-modal.confluence.load-space-label');
    }

    if (!this.selectedSpace) {
      return this.intl.t('knowledge-hub.sync-modal.confluence.select-space-dropdown');
    }

    return this.selectedSpace.name;
  }

  get spacesGroupList() {
    return [
      {
        items: this.spaces.map((space: ConfluenceSpace) => ({
          text: space.name,
          value: space.id,
        })),
      },
    ];
  }

  @action
  selectSpace(spaceId: string) {
    this.selectedSpace = this.spaces.findBy('id', spaceId);

    taskFor(this.fetchFolderSnapshot).perform();

    // Don't reset folder selection if reconfiguring - we'll restore it after loading
    if (!this.args.existingImportSource) {
      this.resetFolderSelection();
    }
  }

  get loadSpacesButtonDisabled() {
    return !this.importSourceId || !this.selectedSiteId;
  }

  @action
  async loadSpaces() {
    if (!this.importSourceId || !this.selectedSiteId) {
      return;
    }

    this.spaces = await taskFor(this.knowledgeHubService.fetchSpacesForSite).perform(
      this.importSourceId,
      this.selectedSiteId,
    );

    // For reconfigure, always restore the previously selected space
    if (
      this.args.existingImportSource &&
      this.importSource?.spaceId &&
      this.importSource?.spaceName
    ) {
      // Backend filters out already-synced spaces, so add it back if missing
      let existingSpace = this.spaces.findBy('id', this.importSource.spaceId);
      if (!existingSpace) {
        this.spaces = [
          {
            id: this.importSource.spaceId,
            name: this.importSource.spaceName,
          } as ConfluenceSpace,
          ...this.spaces,
        ];
      }
      // Always select the space when reconfiguring
      this.selectSpace(this.importSource.spaceId);
    }
  }

  get isLoadingSpaces() {
    return taskFor(this.knowledgeHubService.fetchSpacesForSite).isRunning;
  }

  get syncDisabled() {
    return !this.importSourceId || !this.selectedSiteId || !this.selectedSpaceId;
  }

  @task({ drop: true })
  *startConfluenceAuth(): TaskGenerator<void> {
    if (!this.importSource) {
      let newSource = yield this.knowledgeHubService.createImportSource({
        sourceType: SourceType.Confluence,
        useV2: this.useV2,
      });
      this.importSource = newSource;
    }

    if (!this.importSource) {
      return;
    }

    if (!this.args.existingImportSource || this.args.isReauthenticating) {
      let url = this.knowledgeHubService.getOAuthURL(this.importSource.id);

      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      let window = this.openCenteredWindowService.setup(url, 800, 1280, 'Import');
      if (window) {
        window.focus();
        yield this.waitForWindowClose(window);
      }
    }

    try {
      this.sites = yield taskFor(this.knowledgeHubService.fetchSites).perform(this.importSource.id);
      if (this.sites.length === 1) {
        this.selectSite(this.sites[0].id);
      } else if (this.args.isReconfiguring && this.importSource.siteId) {
        this.selectSite(this.importSource.siteId);
      }
    } catch (error) {
      if (!this.args.isReconfiguring) {
        this.args.onNavigationBack?.();
        this.args.setSyncStep?.(SyncSteps.Connect);
        yield this.importSource.remove({ notify: false });
      }
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.confluence.generic-error'),
      );
    }
  }

  private waitForWindowClose(window: any): Promise<void> {
    return new Promise((resolve) => {
      let checkInterval = setInterval(() => {
        if (window.closed) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 200);
    });
  }

  @action
  async startSync() {
    if (!this.importSource || !this.selectedSiteId || !this.selectedSpace) {
      return;
    }

    // Update confluence import context
    let source = this.importSource;
    source.siteId = this.selectedSiteId;
    source.spaceId = this.selectedSpace.id;
    source.spaceName = this.selectedSpace.name;

    let denyFolderIds: string[] = [];
    if (this.selectedFolderIds.size > 0) {
      denyFolderIds = this.folders
        .filter((folder) => !this.selectedFolderIds.has(folder.id))
        .map((f) => f.id);
    }

    source.denyFolderIds = denyFolderIds;
    source.useV2 = this.useV2;

    if (this.selectedSite?.url) {
      source.baseUrl = this.selectedSite?.url;
    }
    await source.save();

    this.knowledgeHubService.startImport(this.importSource.id, this.syncBehavior);
    this.args.onModalClose();

    let notificationMessage =
      this.syncBehavior === SyncBehaviorType.Sync
        ? this.intl.t('knowledge-hub.sync-modal.confluence.sync-started')
        : this.intl.t('knowledge-hub.sync-modal.confluence.import-started');

    this.knowledgeHubService.notifySourceStateChangeConfirmation(
      notificationMessage,
      this.importSource.id,
    );
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      context: 'import-content',
      object: `import-content.confluence`,
      section: 'knowledge-hub.new-content.confluence',
      place: 'knowledge-hub.new-content',
    });
  }

  @action
  updateSelectedFolders(selectedFolderIds: Set<string>) {
    this.selectedFolderIds = selectedFolderIds;
  }

  @action
  async toggleUseV2() {
    let wasV1TogglingToV2 = !this.useV2 && this.args.isReconfiguring;

    this.previousUseV2Value = this.useV2;
    this.useV2 = !this.useV2;

    if (this.importSource) {
      this.importSource.useV2 = this.useV2;

      // Save to trigger backend update
      // This triggers the new folder tree to be fetched
      await this.importSource.save();

      // Reload folder tree if we have a selected space (v2 may have different folders)
      if (this.selectedSpaceId) {
        await taskFor(this.fetchFolderSnapshot).perform();

        // When toggling v1→v2 during reconfigure, deselect non-page types at level 1 and their children
        // This preserves their original page selections only
        if (wasV1TogglingToV2) {
          let newSelectedIds = new Set(this.selectedFolderIds);

          // Find all level 1 non-page items and their descendants
          let foldersToDeselect = new Set<string>();
          this.folders.forEach((folder) => {
            if (folder.type !== 'page' && folder.level === 1) {
              foldersToDeselect.add(folder.id);
            }
          });

          // Recursively add all descendants of folders to deselect
          let addDescendants = (parentId: string) => {
            this.folders.forEach((folder) => {
              if (folder.parent_id === parentId) {
                foldersToDeselect.add(folder.id);
                addDescendants(folder.id);
              }
            });
          };

          foldersToDeselect.forEach((folderId) => addDescendants(folderId));

          foldersToDeselect.forEach((id) => newSelectedIds.delete(id));

          this.selectedFolderIds = newSelectedIds;
        }
      }
    }
  }

  get showV2ToV1Warning() {
    return this.previousUseV2Value && !this.useV2;
  }

  get shouldShowUseV2Toggle() {
    return this.args.isReconfiguring;
  }

  @action
  async loadChildrenForFolder(folderId: string, itemType: string) {
    // Called when user clicks on a node with empty children_ids
    // to check if it has any unloaded children
    // Mark the folder as loading
    this.folders = this.folders.map((folder) =>
      folder.id === folderId ? { ...folder, is_loading: true } : folder,
    );

    try {
      let parent = this.folders.find((f) => f.id === folderId);
      if (!parent) {
        return;
      }

      let children = await this.fetchFolderTreeSnapshot(folderId, itemType);

      if (children.length > 0) {
        // Backend always returns levels 1-5, we need to adjust based on parent level
        // If parent is at level 5, children should be 6-10
        let levelOffset = parent.level;
        let adjustedChildren = children.map((child) => ({
          ...child,
          level: child.level + levelOffset,
        }));

        // Update parent with children IDs and remove loading state
        this.folders = this.folders.map((folder) => {
          if (folder.id === folderId) {
            return {
              ...folder,
              children_ids: adjustedChildren.map((c) => c.id),
              is_loading: false,
              has_more_children: false,
            };
          }
          return folder;
        });

        // Insert children immediately after parent to maintain hierarchy order
        let parentIndex = this.folders.findIndex((f) => f.id === folderId);
        if (parentIndex !== -1) {
          this.folders = [
            ...this.folders.slice(0, parentIndex + 1), // Everything up to and including parent
            ...adjustedChildren, // New children with adjusted levels
            ...this.folders.slice(parentIndex + 1), // Everything after parent
          ];
        } else {
          // Fallback: append at end if parent not found (shouldn't happen)
          this.folders = this.folders.concat(adjustedChildren);
        }

        // Restore folder selection from deny list for newly loaded children
        if (this.args.existingImportSource) {
          this.restoreFolderSelectionFromDenyList();
        }
      } else {
        // Node has no children, remove loading state and mark as checked
        this.folders = this.folders.map((folder) =>
          folder.id === folderId
            ? { ...folder, is_loading: false, has_more_children: false }
            : folder,
        );
      }
    } catch (error: unknown) {
      // On error, remove loading state and mark as checked so user can select
      this.folders = this.folders.map((folder) =>
        folder.id === folderId
          ? { ...folder, is_loading: false, has_more_children: false }
          : folder,
      );
      let errorMessage =
        (error as any)?.jqXHR?.responseJSON?.error ||
        this.intl.t('knowledge-hub.sync-modal.confluence.generic-error');
      this.notificationsService.notifyError(errorMessage);
    }
  }

  @task({ drop: true })
  *fetchFolderSnapshot(): TaskGenerator<void> {
    if (!this.importSourceId || !this.selectedSiteId || !this.selectedSpaceId) {
      return;
    }

    try {
      this.folders = yield this.fetchFolderTreeSnapshot();

      // For reconfigure, restore the previously selected folders
      if (this.args.existingImportSource) {
        this.restoreFolderSelectionFromDenyList();
      }
    } catch (error: unknown) {
      let errorMessage =
        (error as any)?.jqXHR?.responseJSON?.error ||
        this.intl.t('knowledge-hub.sync-modal.confluence.generic-error');
      this.notificationsService.notifyError(errorMessage);
    }
  }

  async fetchFolderTreeSnapshot(
    nodeId: string | null = null,
    nodeType: string | null = null,
  ): Promise<FolderTreeItem[]> {
    let params: Record<string, any> = {
      app_id: this.appService.app.id,
      import_source_id: this.importSourceId,
      site_id: this.selectedSiteId,
      space_id: this.selectedSpaceId,
    };

    params.depth = 5;

    if (nodeId) {
      params.parent_id = nodeId;
      params.type = nodeType;
    }

    let response = await get('/ember/import_service/import_sources/confluence/folder_tree', params);

    let json = response as unknown as FolderTreeItem[];
    return json;
  }

  get isLoadingFolders() {
    return taskFor(this.fetchFolderSnapshot).isRunning;
  }

  get folderSelectorLabel() {
    if (this.isLoadingFolders) {
      return this.intl.t('knowledge-hub.sync-modal.folder-selector.loading-collection');
    }

    return undefined;
  }

  private resetSpaceSelection() {
    this.spaces = [];
    this.selectedSpace = undefined;
    this.resetFolderSelection();
  }

  private resetFolderSelection() {
    this.folders = [];
    this.selectedFolderIds = new Set();
  }

  private getAllDeniedFoldersWithDescendants(denyFolderIds: string[]): Set<string> {
    let deniedSet = new Set<string>(denyFolderIds);
    let folderMap = new Map(this.folders.map((f) => [f.id, f]));

    let addDescendants = (folderId: string) => {
      let folder = folderMap.get(folderId);
      if (folder?.children_ids) {
        for (let childId of folder.children_ids) {
          deniedSet.add(childId);
          addDescendants(childId);
        }
      }
    };

    denyFolderIds.forEach(addDescendants);
    return deniedSet;
  }

  private restoreFolderSelectionFromDenyList() {
    let denyFolderIds = this.importSource?.context?.deny_folder_ids;

    if (denyFolderIds && denyFolderIds.length > 0) {
      let deniedWithDescendants = this.getAllDeniedFoldersWithDescendants(denyFolderIds);
      this.selectedFolderIds = new Set(
        this.folders.filter((f) => !deniedWithDescendants.has(f.id)).map((f) => f.id),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Confluence': typeof ConfluenceSyncModal;
    'knowledge-hub/setup-modal/sync-modal/confluence': typeof ConfluenceSyncModal;
  }
}
